// apps/client/src/pages/leads/edm/components/SegmentPicker.tsx
//
// Saved audience segments (S4). Choosing a segment COPIES its spec into this
// campaign's audience; there is no live link, so editing or deleting a
// segment later never changes a campaign. "Save as segment" stores the
// current audience under a name. The list loads when the picker is opened,
// so opening the editor makes no eDM call.

import React, { useState } from 'react';
import { Bookmark, Trash2 } from 'lucide-react';
import ConfirmDialog from '../../../../components/common/ConfirmDialog';
import { EDM_UI, edmErrorMessage } from '../../../../utils/edm';
import type { EdmAudience, EdmSegment } from '../../../../hooks/useEdmCampaigns';

const SegmentPicker: React.FC<{
  audience: EdmAudience;
  /** False when no source is ticked: an empty spec is not worth saving. */
  canSaveCurrent: boolean;
  onApply: (spec: EdmAudience) => void;
  listSegments: () => Promise<{ segments: EdmSegment[] }>;
  saveSegment: (params: { name: string; spec: EdmAudience }) => Promise<{ id: string }>;
  deleteSegment: (segmentId: string) => Promise<unknown>;
  disabled?: boolean;
}> = ({ audience, canSaveCurrent, onApply, listSegments, saveSegment, deleteSegment, disabled }) => {
  const [open, setOpen] = useState(false);
  const [segments, setSegments] = useState<EdmSegment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<EdmSegment | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listSegments();
      setSegments(Array.isArray(res?.segments) ? res.segments : []);
    } catch (err) {
      setError(edmErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    setNotice(null);
    if (next && segments === null && !loading) void load();
  };

  const apply = (s: EdmSegment) => {
    onApply(s.spec);
    setNotice(`Audience set from "${s.name}".`);
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await saveSegment({ name: trimmed, spec: audience });
      setNaming(false);
      setName('');
      setNotice(`Saved segment "${trimmed}".`);
      if (open || segments !== null) await load();
    } catch (err) {
      setError(edmErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s: EdmSegment) => {
    setBusy(true);
    setError(null);
    try {
      await deleteSegment(s.id);
      setNotice(`Deleted segment "${s.name}".`);
      await load();
    } catch (err) {
      setError(edmErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (disabled) return null;

  return (
    <div className="space-y-2" data-testid="edm-segment-picker">
      <span className={EDM_UI.label}>Saved segments</span>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={EDM_UI.secondaryButton} onClick={toggleOpen} aria-expanded={open}>
          <Bookmark className="h-3.5 w-3.5" /> {open ? 'Hide segments' : 'Use a saved segment'}
        </button>
        <button
          type="button"
          className={EDM_UI.secondaryButton}
          disabled={!canSaveCurrent || busy}
          onClick={() => {
            setNaming((n) => !n);
            setNotice(null);
          }}
        >
          Save as segment
        </button>
      </div>
      {naming && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-0 flex-1">
            <label htmlFor="edm-segment-name" className={EDM_UI.label}>
              Segment name
            </label>
            <input
              id="edm-segment-name"
              className={EDM_UI.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <button type="button" className={EDM_UI.primaryButton} disabled={!name.trim() || busy} onClick={() => void save()}>
            Save segment
          </button>
        </div>
      )}
      {open && (
        <div className="rounded-md border border-gray-700 bg-gray-900/40 p-1">
          {loading ? (
            <div className="px-2 py-3 text-xs text-gray-500">Loading segments…</div>
          ) : (segments?.length ?? 0) === 0 ? (
            <div className="px-2 py-3 text-xs text-gray-500">No saved segments yet.</div>
          ) : (
            (segments ?? []).map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-gray-800">
                <span className="truncate text-xs text-gray-200">{s.name}</span>
                <span className="flex flex-shrink-0 items-center gap-2">
                  <button type="button" className={EDM_UI.secondaryButton} disabled={busy} onClick={() => apply(s)}>
                    Use
                  </button>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-red-300 disabled:opacity-50"
                    disabled={busy}
                    onClick={() => setToDelete(s)}
                    aria-label={`Delete segment ${s.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
            ))
          )}
        </div>
      )}
      {notice && <p className="text-xs text-emerald-300">{notice}</p>}
      {error && <p className="text-xs text-red-300">{error}</p>}
      <p className="text-xs text-gray-500">
        Using a segment copies its settings into this campaign. Later changes to the segment do not affect it.
      </p>
      <ConfirmDialog
        isOpen={toDelete !== null}
        title="Delete this segment?"
        message="Campaigns that already used it keep their audience."
        confirmLabel="Delete segment"
        confirmVariant="danger"
        onCancel={() => setToDelete(null)}
        onConfirm={() => {
          const s = toDelete;
          setToDelete(null);
          if (s) void remove(s);
        }}
      />
    </div>
  );
};

export default SegmentPicker;
